import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Popconfirm, Space, Tag, Tooltip, Typography, message } from 'antd';
import {
    AutoSellWhen,
    ExRightsStatus,
    getExRightsStatus,
    requestAutoSell,
} from '../../services/exRightsApi';
import { formatInt } from '../../utils/numberFormat';

const { Text } = Typography;

/**
 * "This symbol is about to stop being tradable, and here is your choice."
 *
 * Every other part of this feature is a refusal — the save, the deposit, the import, the
 * bot's next buy. A refusal is not a notice: it arrives when the user tries to do something,
 * which is the wrong moment and possibly never. This panel is the only place the window is
 * stated before it bites.
 *
 * It is NOT read-only, and that is the difference from `CorporateActionPanel` sitting beside
 * it. Whether a config's numbers get adjusted for an ex-rights event is platform work — the
 * same fact for every account. Whether to SELL a position before that date is the owner's
 * money and nobody else's call, so the buttons belong here.
 *
 * The three states it has to tell apart, because acting on the wrong one costs real money:
 *
 *   deadline + nothing decided   act, or the wallet is closed and the shares go to the
 *                                basic book on that morning
 *   deadline + already answered  say which answer, and whether it has been armed yet
 *   no deadline                  the ban stops new positions and forces nobody out. This
 *                                one has no buttons at all: offering "sell it out" here
 *                                would invent a deadline the admin deliberately did not set
 */

/** ISO instant → the date a user reads. Time of day is noise in a deadline measured in days. */
function onDate(iso: string | null): string {
    return iso ? iso.slice(0, 10) : '—';
}

/**
 * What each choice actually costs, in the user's terms rather than the enum's.
 *
 * `NEXT_START` carries a risk worth saying out loud: a bot that is never started again never
 * sells, and the deadline arrives anyway.
 */
const WHEN_LABELS: Record<AutoSellWhen, string> = {
    NOW: 'Sell now',
    NEXT_START: 'Sell at next start',
};

interface Props {
    dataConfigId?: string | null;
    /** Called after an answer is recorded, so the page can re-read whatever it shows. */
    onChanged?: () => void;
}

export const ExRightsPanel: React.FC<Props> = ({ dataConfigId, onChanged }) => {
    const [rows, setRows] = useState<ExRightsStatus[] | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!dataConfigId) {
            setRows(null);
            return;
        }
        try {
            setRows(await getExRightsStatus(dataConfigId));
        } catch {
            // Nothing to show is the honest fallback: this panel explains, it does not gate.
            // The refusals downstream hold whether or not it rendered.
            setRows(null);
        }
    }, [dataConfigId]);

    useEffect(() => {
        void load();
    }, [load]);

    const answer = async (row: ExRightsStatus, when: AutoSellWhen) => {
        setBusy(row.instrumentConfigId);
        try {
            const out = await requestAutoSell(row.instrumentConfigId, when);
            message.success(
                out.applied
                    ? `${row.symbol}: selling out now — the bot stops buying and closes the position at market.`
                    : `${row.symbol}: recorded. It will be sold the next time this config starts.`,
            );
            await load();
            onChanged?.();
        } catch (e: any) {
            message.error(
                e?.response?.data?.message ?? `Could not record the instruction for ${row.symbol}.`,
            );
        } finally {
            setBusy(null);
        }
    };

    if (!dataConfigId || !rows || rows.length === 0) return null;

    return (
        <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 12 }}>
            {rows.map((row) => {
                const decided = row.autoSell;
                const held = row.heldQty > 0;
                // No deadline is a different message, not a missing date: the ban stops new
                // positions and leaves the holder where they are.
                const forced = row.retireAfter != null;

                return (
                    <Alert
                        key={row.instrumentConfigId}
                        type={forced && !decided && held ? 'warning' : 'info'}
                        showIcon
                        message={
                            <Space wrap size={6}>
                                <Text strong>{row.symbol}</Text>
                                <Text>cannot be traded</Text>
                                {row.endsAt && (
                                    <Text type="secondary">until {onDate(row.endsAt)}</Text>
                                )}
                                {decided && (
                                    <Tag color={decided.applied ? 'green' : 'blue'}>
                                        {WHEN_LABELS[decided.when]}
                                        {decided.applied ? ' · armed' : ' · waiting'}
                                    </Tag>
                                )}
                            </Space>
                        }
                        description={
                            <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {row.reason}
                                </Text>

                                {/* What the block does and does not stop. Said every time,
                                    because "cannot be traded" reads as a freeze, and a
                                    holder who believes the exit is shut behaves differently
                                    at exactly the moment it matters. */}
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    You can still <Text strong>sell</Text> and the grid keeps
                                    taking profit. What stops is <Text strong>opening</Text>:
                                    new buys, funding this symbol, and adding it to a config.
                                </Text>

                                {held && forced && (
                                    <Text style={{ fontSize: 12 }}>
                                        You hold <Text strong>{formatInt(row.heldQty)}</Text>{' '}
                                        share(s). If you do nothing, on{' '}
                                        <Text strong>{onDate(row.retireAfter)}</Text> this
                                        wallet is closed and they move to your{' '}
                                        <Text strong>basic holdings</Text> — the shares stay
                                        yours at the broker, the grid simply stops claiming
                                        them. You can import them back afterwards.
                                    </Text>
                                )}

                                {held && !forced && (
                                    <Text style={{ fontSize: 12 }}>
                                        You hold <Text strong>{formatInt(row.heldQty)}</Text>{' '}
                                        share(s) and <Text strong>keep them</Text>: this block
                                        has no deadline, so nothing is taken from the grid.
                                    </Text>
                                )}

                                {row.canAutoSell && held && !decided?.applied && (
                                    <Space wrap>
                                        <Popconfirm
                                            title={`Sell all ${formatInt(row.heldQty)} ${row.symbol} now?`}
                                            description="The bot stops buying and closes the position at market. This cannot be undone."
                                            okText="Sell now"
                                            cancelText="Cancel"
                                            onConfirm={() => answer(row, 'NOW')}
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                loading={busy === row.instrumentConfigId}
                                            >
                                                Sell now
                                            </Button>
                                        </Popconfirm>
                                        <Tooltip title="Nothing happens until this config is started again. A bot that is never started again never sells, and the shares move to your basic holdings on the date above.">
                                            <Button
                                                size="small"
                                                loading={busy === row.instrumentConfigId}
                                                onClick={() => answer(row, 'NEXT_START')}
                                            >
                                                Sell at next start
                                            </Button>
                                        </Tooltip>
                                    </Space>
                                )}
                            </Space>
                        }
                    />
                );
            })}
        </Space>
    );
};

export default ExRightsPanel;
